/**
 * Skills Planner pipeline: SETA format specs, schema normalization for /helper,
 * RPC → formatedData (edge-aligned), and AI helper + parser execution.
 *
 * Shared backend POST: `postBackendHelper` / `postBackendHelperContent` in `./backendHelperApi.js`.
 */
import { isUnemployedEmploymentType } from "../constant/utils/employmentTypeUtils";
import { postBackendHelperContent } from "./backendHelperApi";

// =============================================================================
// SETA-driven input/output format specs (template fields[] only)
// =============================================================================

/**
 * Skills Planner–style I/O format specs derived **only** from SETA template `fields[]`.
 * Employment-type metadata comes from the **Employee Type** field’s `allowedValues` when
 * they are objects `{ label, value, isUnemployedType? }` (same as file_structure employmentTypes).
 * Does not use get_training_forms rows or live payloads.
 */

/**
 * Employment types for plan rules (unemployed / stipend / single-training), read from the
 * SETA field with config.isEmploymentTypeField — not a separate sibling array.
 * @param {Array} fields - setaTemplate fields (enriched)
 * @returns {Array<{ label?: string, value?: string, isUnemployedType?: boolean }>}
 */
export function employmentTypesFromSetaFields(fields) {
  if (!Array.isArray(fields)) return [];
  const f = fields.find((x) => x?.config?.isEmploymentTypeField);
  const allowed = f?.allowedValues;
  if (!Array.isArray(allowed) || allowed.length === 0) return [];
  const allObjects = allowed.every(
    (o) => o != null && typeof o === "object" && !Array.isArray(o)
  );
  if (!allObjects) return [];
  return allowed.map((o) => ({
    label: o.label != null ? String(o.label) : o.value != null ? String(o.value) : "",
    value: o.value != null ? String(o.value) : o.label != null ? String(o.label) : "",
    isUnemployedType: Boolean(o.isUnemployedType),
  }));
}

/**
 * Catalogue / join keys always or commonly on trainingsAllocated[] after RPC mapping
 * (not SETA field names — those come from applySetaAliases). Real key names only — embedded in specs for /helper.
 */
export const TRAININGS_ALLOCATED_STATIC_KEYS = [
  "cost",
  "type",
  "training_category",
  "skill_requirements_id",
  "skill_required",
  "allocated_additional_field",
  "id",
  "name",
  "provider",
  "nqf_level",
  "description",
  "nqf_aligned",
  "catalogue_id",
  "targeted_skills",
  "program_duration",
  "url",
];

function isAdditionalAllocationField(field) {
  return Boolean(field?.config?.isAdditionalField);
}

/**
 * SETA fieldNames stored on company_user.data (empDetails) for structured plan input.
 */
export function listEmpDetailsFieldNames(fields) {
  if (!Array.isArray(fields)) return [];
  return fields
    .filter((f) => {
      const c = f?.config || {};
      if (!f?.fieldName) return false;
      if (isAdditionalAllocationField(f)) return false;
      if (c.isTrainingCostField || c.isTrainingTypeField || c.isTrainingProviderField) {
        return false;
      }
      if (
        c.isEmpId ||
        c.isFirstNameField ||
        c.isLastNameField ||
        c.isEmailField ||
        c.isGenderField ||
        c.isRaceField ||
        c.isDisabilityField ||
        c.isEmploymentTypeField ||
        c.isDepartmentNameField ||
        c.employeeImport
      ) {
        return true;
      }
      if (c.exportSheet && c.generatePlan && c.employeeImport) return true;
      return false;
    })
    .map((f) => f.fieldName);
}

export function listAdditionalAllocationFieldDefs(fields) {
  if (!Array.isArray(fields)) return [];
  return fields
    .filter((f) => isAdditionalAllocationField(f) && f.fieldName)
    .map((f) => ({
      fieldName: f.fieldName,
      type: f.type,
      fieldDescription: f.fieldDescription,
    }));
}

/** SETA columns that belong to the training/export line, not to empDetails.data (complement of employee keys). */
export function listTrainingLineSetaFieldNames(fields) {
  const emp = new Set(listEmpDetailsFieldNames(fields));
  if (!Array.isArray(fields)) return [];
  return fields.map((f) => f.fieldName).filter((name) => name && !emp.has(name));
}

export function buildSkillsPlanInputFormatSpec(fields) {
  const empKeys = listEmpDetailsFieldNames(fields);
  const additionalDefs = listAdditionalAllocationFieldDefs(fields);
  const trainingLineSetaNames = listTrainingLineSetaFieldNames(fields);
  const employmentTypes = employmentTypesFromSetaFields(fields);

  return {
    version: 1,
    recordDescription:
      "One element of formatedData[] for structured Skills Planner (same idea as generateStructuredPlan input).",
    /**
     * Use this to verify parser inputSchema / helper prompt matches real payloads from get_training_forms → formatedData.
     */
    alignment: {
      matchesEdgeGenerateStructuredPlan: true,
      topLevelKeysMustBe: ["empDetails", "trainingsAllocated", "skillRequirements", "emp_id", "dept_id"],
      empDetails: {
        source: "empData.data from get_training_forms",
        keysAreSetaFieldNames: true,
        note: "Keys should match SETA employee columns (subset ok if optional fields empty).",
      },
      trainingsAllocated: {
        source: "Map of selected_trainings + training row + allocate_training_program",
        catalogueKeys:
          "Items are spread from training_catalogue_data (name, cost, provider, …). Edge / dev sample also writes SETA fieldName keys on the same object from template config — use those for export-shaped reads when present.",
        mappedKeys:
          "Always: cost, type, training_category, skill_requirements_id, skill_required, allocated_additional_field.",
        allocated_additional_field:
          "Object keys = SETA fieldName for fields with config.isAdditionalField (e.g. Programme Level, Start Date).",
        setaColumnNamesAlsoOnTrainingRow:
          trainingLineSetaNames,
        parserNote:
          "AI parser maps catalogue fields + allocated_additional_field + empDetails → output SETA fieldName keys per row.",
      },
      skillRequirements: {
        source: "skill_requirements[] from RPC; join via skill_requirements_id on each training line.",
      },
      emp_id_dept_id: {
        source: "company_user.emp_id (text) and department_id",
      },
    },
    topLevelProperties: {
      empDetails: {
        type: "object",
        source: "company_user.data",
        expectedKeysFromTemplate: empKeys,
      },
      trainingsAllocated: {
        type: "array",
        source:
          "selected_trainings + training_catalogue_data + allocate_training_program",
        item: {
          staticKeys: TRAININGS_ALLOCATED_STATIC_KEYS,
          setaColumnNamesAlsoOnTrainingRow: trainingLineSetaNames,
          allocated_additional_field: {
            type: "object",
            keysFromSeta_isAdditionalField: additionalDefs.map((d) => d.fieldName),
            fieldDefinitions: additionalDefs,
          },
        },
      },
      skillRequirements: {
        type: "array",
        source: "skill_requirements for the form",
      },
      emp_id: { type: "string", source: "company_user.emp_id" },
      dept_id: { type: "string", source: "company_user.department_id" },
    },
    dynamics: {
      employmentTypesNote:
        "Stipend / single-training rules use emp employment_type vs Employee Type allowedValues (isUnemployedType). Sourced from SETA template field with isEmploymentTypeField, not a separate array.",
      employmentTypes,
    },
  };
}

export function buildSkillsPlanOutputFormatSpec(fields) {
  if (!Array.isArray(fields)) {
    return {
      version: 1,
      transformMode: "oneRowPerTrainingItem",
      description: "No SETA fields.",
      costFieldNameOverwrittenFromDb: null,
      fields: [],
    };
  }

  const costField = fields.find((f) => f?.config?.isTrainingCostField)?.fieldName ?? null;

  return {
    version: 1,
    transformMode: "oneRowPerTrainingItem",
    description:
      "One plain object per trainingsAllocated[] row; keys must match SETA fieldName. Cost column forced from DB after AI when isTrainingCostField is set.",
    alignment: {
      matchesEdgePostProcess: true,
      oneOutputObjectPer: "trainingsAllocated[] element (same order)",
      rowKeysAre: "SETA template fieldName strings (see fields[] below)",
      costFieldOverwrittenAfterParser: costField,
      allocatedAdditionalMergedOntoRow: true,
    },
    costFieldNameOverwrittenFromDb: costField,
    rules: [
      "Use exact fieldName strings as keys (including spaces).",
      "Merge allocated_additional_field onto row.",
      "Dropdown fields: copy from input; label/value alignment runs at parse time (not in this schema).",
    ],
    fields: fields
      .filter((f) => f?.fieldName && f?.config?.generatePlan)
      .map((f) => ({
        fieldName: f.fieldName,
        type: f.type,
        fieldDescription: f.fieldDescription,
      })),
  };
}

// =============================================================================
// Normalize fat format specs → slim inputSchema / outputSchema for /helper
// =============================================================================

/**
 * Normalize fat format specs (buildSkillsPlanInputFormatSpec / outputDataFormat)
 * into the slimmer inputSchema / outputSchema shapes expected by Parser Lab's
 * default /helper prompt (version, recordDescription, properties, dynamics; output fields without flags).
 */

function unwrapInputDataFormat(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  if (
    Object.prototype.hasOwnProperty.call(raw, "inputDataFormat") &&
    Object.keys(raw).length === 1
  ) {
    return raw.inputDataFormat;
  }
  return raw;
}

function unwrapOutputDataFormat(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  if (
    Object.prototype.hasOwnProperty.call(raw, "outputDataFormat") &&
    Object.keys(raw).length === 1
  ) {
    return raw.outputDataFormat;
  }
  return raw;
}

const STATIC_TRAINING_KEYS_FALLBACK = [
  "cost",
  "type",
  "training_category",
  "skill_requirements_id",
  "skill_required",
  "allocated_additional_field",
  "id",
  "name",
  "provider",
  "nqf_level",
  "description",
  "nqf_aligned",
  "catalogue_id",
  "targeted_skills",
  "program_duration",
];

const TRAININGS_ALLOCATED_INPUT_HINT =
  "trainingsAllocated[]: catalogue keys plus SETA fieldName duplicates when pipeline merged them; merge allocated_additional_field on output.";

function itemFieldsFromTopLevelTrainings(trainingsBlock) {
  const item = trainingsBlock?.item;
  const staticKeys = Array.isArray(item?.staticKeys) ? item.staticKeys : [];
  const addl =
    item?.allocated_additional_field?.fieldDefinitions ||
    item?.allocated_additional_field?.keysFromSeta_isAdditionalField;

  const names = new Set();
  for (const k of staticKeys) {
    if (typeof k !== "string" || k.includes("…") || k.includes("spread")) continue;
    names.add(k.trim());
  }
  if (names.size === 0) {
    STATIC_TRAINING_KEYS_FALLBACK.forEach((k) => names.add(k));
  }

  const itemFields = [];
  for (const name of names) {
    if (name === "allocated_additional_field") {
      const keys = Array.isArray(addl)
        ? addl.map((x) => (typeof x === "string" ? x : x?.fieldName)).filter(Boolean)
        : [];
      itemFields.push({
        name: "allocated_additional_field",
        type: "object",
        description:
          keys.length > 0
            ? `Additional SETA fields. Typical keys: ${keys.join(", ")}`
            : "Additional SETA fields keyed by SETA fieldName",
      });
    } else {
      const t =
        name === "cost"
          ? "number"
          : name === "nqf_aligned"
            ? "boolean"
            : "string";
      itemFields.push({ name, type: t, nullable: true });
    }
  }
  return itemFields;
}

/**
 * Convert buildSkillsPlan-style inputDataFormat → prompt-friendly inputSchema.
 */
export function slimInputSchemaForHelper(spec) {
  const s = unwrapInputDataFormat(spec);
  if (!s || typeof s !== "object") return spec;

  const tp = s.topLevelProperties;
  if (!tp || typeof tp !== "object") {
    const { alignment: _a, inputDataFormat: _i, ...rest } = s;
    return rest;
  }

  const empKeys = tp.empDetails?.expectedKeysFromTemplate;
  const empFields = Array.isArray(empKeys)
    ? empKeys.map((name) => ({ name, type: "string" }))
    : [];

  const out = {
    version: typeof s.version === "number" ? s.version : 1,
    recordDescription:
      s.recordDescription ||
      "One formatedData record: empDetails, trainingsAllocated[], skillRequirements[], emp_id, dept_id.",
    properties: {
      empDetails: {
        type: "object",
        description: tp.empDetails?.source || "Employee SETA-shaped key/value object",
        fields: empFields,
      },
      trainingsAllocated: {
        type: "array",
        description: [TRAININGS_ALLOCATED_INPUT_HINT, tp.trainingsAllocated?.source]
          .filter(Boolean)
          .join(" "),
        itemFields: itemFieldsFromTopLevelTrainings(tp.trainingsAllocated),
      },
      skillRequirements: {
        type: "array",
        description: tp.skillRequirements?.source || "skill_requirements rows for the form",
      },
      emp_id: { type: "string", description: "company_user.emp_id (text)" },
      dept_id: { type: "string", description: "department id" },
    },
    dynamics: {},
  };

  if (Array.isArray(s.dynamics?.employmentTypes) && s.dynamics.employmentTypes.length) {
    out.dynamics.employmentTypes = s.dynamics.employmentTypes;
  }

  return out;
}

/**
 * Slim outputDataFormat → outputSchema for /helper (no flags, no alignment).
 */
export function slimOutputSchemaForHelper(spec) {
  const s = unwrapOutputDataFormat(spec);
  if (!s || typeof s !== "object") return spec;

  const rawFields = s.fields;
  if (!Array.isArray(rawFields)) {
    const { alignment: _a, outputDataFormat: _o, ...rest } = s;
    return rest;
  }

  const fields = rawFields.map((f) => ({
    fieldName: f.fieldName,
    type: f.type,
    fieldDescription: f.fieldDescription || "",
  }));

  return {
    version: typeof s.version === "number" ? s.version : 1,
    description: s.description || "",
    transformMode: s.transformMode,
    rules: Array.isArray(s.rules) ? s.rules : [],
    fields,
  };
}

function isFatInputSchema(obj) {
  if (!obj || typeof obj !== "object") return false;
  if (obj.topLevelProperties != null) return true;
  if (obj.alignment != null) return true;
  if (
    Object.keys(obj).length === 1 &&
    Object.prototype.hasOwnProperty.call(obj, "inputDataFormat")
  ) {
    return true;
  }
  return false;
}

function isFatOutputSchema(obj) {
  if (!obj || typeof obj !== "object") return false;
  if (obj.alignment != null) return true;
  if (
    Object.keys(obj).length === 1 &&
    Object.prototype.hasOwnProperty.call(obj, "outputDataFormat")
  ) {
    return true;
  }
  const f = obj.fields;
  if (
    Array.isArray(f) &&
    f.some(
      (row) =>
        row != null &&
        typeof row === "object" &&
        Object.prototype.hasOwnProperty.call(row, "flags")
    )
  ) {
    return true;
  }
  return false;
}

/**
 * Returns { inputSchema, outputSchema } for /helper (field names + types only; no allowedValues).
 */
export function normalizeSchemasForParserHelper(inputSchema, outputSchema) {
  let inS = unwrapInputDataFormat(inputSchema);
  let outS = unwrapOutputDataFormat(outputSchema);

  if (isFatOutputSchema(outS)) {
    outS = slimOutputSchemaForHelper(outS);
  }

  if (isFatInputSchema(inS)) {
    inS = slimInputSchemaForHelper(inS);
  } else {
    const { alignment: _a, ...restIn } = inS || {};
    inS = restIn;
  }

  const { alignment: _ao, ...restOut } = outS || {};
  outS = restOut;

  return { inputSchema: inS, outputSchema: outS };
}

// =============================================================================
// RPC row → formatedData (aligned with generateTraining edge)
// =============================================================================

function listEmpDetailFieldNamesForAlias(rawFields) {
  const names = new Set();
  if (!Array.isArray(rawFields)) return names;
  for (const f of rawFields) {
    const c = f?.config || {};
    const fn = f?.fieldName;
    if (!fn) continue;
    if (c.isAdditionalField) continue;
    if (c.isTrainingCostField || c.isTrainingTypeField || c.isTrainingProviderField) continue;
    if (
      c.isEmpId ||
      c.isFirstNameField ||
      c.isLastNameField ||
      c.isEmailField ||
      c.isGenderField ||
      c.isRaceField ||
      c.isDisabilityField ||
      c.isEmploymentTypeField ||
      c.isDepartmentNameField ||
      c.employeeImport
    ) {
      names.add(fn);
    } else if (c.exportSheet && c.generatePlan && c.employeeImport) {
      names.add(fn);
    }
  }
  return names;
}

function applySetaAliasesToTrainingRow(row, rawSetaFields) {
  if (!row || typeof row !== "object") return row;
  const out = { ...row };
  if (!Array.isArray(rawSetaFields) || rawSetaFields.length === 0) return out;

  const empNames = listEmpDetailFieldNamesForAlias(rawSetaFields);
  let programmeFieldName = null;
  for (const f of rawSetaFields) {
    const c = f?.config || {};
    const fn = f?.fieldName;
    if (!fn || empNames.has(fn)) continue;
    if (
      c.generatePlan &&
      f.type === "string" &&
      !c.isAdditionalField &&
      !c.isTrainingCostField &&
      !c.isTrainingProviderField &&
      !c.isTrainingTypeField
    ) {
      programmeFieldName = fn;
      break;
    }
  }
  if (programmeFieldName != null && row.name != null && row.name !== "") {
    out[programmeFieldName] = row.name;
  }
  for (const f of rawSetaFields) {
    const fn = f?.fieldName;
    if (!fn) continue;
    const c = f?.config || {};
    if (c.isTrainingCostField && row.cost != null && row.cost !== "") out[fn] = row.cost;
    if (c.isTrainingProviderField && row.provider != null && row.provider !== "") out[fn] = row.provider;
    if (c.isTrainingTypeField && row.type != null && row.type !== "") out[fn] = row.type;
  }
  const nqfField = rawSetaFields.find(
    (f) =>
      typeof f?.fieldName === "string" &&
      /nqf/i.test(f.fieldName) &&
      /aligned/i.test(f.fieldName)
  );
  if (nqfField?.fieldName && typeof row.nqf_aligned === "boolean") {
    out[nqfField.fieldName] = row.nqf_aligned ? "Yes" : "No";
  }
  return out;
}

/** One formatedData record (same mapping as generateTraining edge). */
export function formatedDataRecordFromRpcRow(
  emp,
  employmentTypes = [],
  setaTemplateFields = null
) {
  if (!emp || typeof emp !== "object") return null;
  const { empData, selectedTrainings, skillRequirements } = emp;
  const isUnemployed = isUnemployedEmploymentType(
    employmentTypes,
    empData?.employment_type
  );
  const stipendAmount = Number(empData?.stipend_amount ?? 0) || 0;
  const trainingsToUse = Array.isArray(selectedTrainings)
    ? isUnemployed
      ? selectedTrainings.slice(0, 1)
      : selectedTrainings
    : [];

  const trainingsAllocated = trainingsToUse.map((trainings) => {
    const programmeCost = Number(trainings?.training?.cost || 0) || 0;
    const totalRowCost = isUnemployed ? programmeCost + stipendAmount : programmeCost;
    const row = {
      ...trainings?.training,
      cost: isUnemployed ? totalRowCost : programmeCost,
      type:
        trainings?.allocated_training_type ||
        trainings?.training?.type ||
        null,
      training_category:
        trainings?.allocated_training_category ||
        trainings?.training?.training_category ||
        null,
      skill_requirements_id: trainings?.skill_requirements_id,
      skill_required: Array.isArray(skillRequirements)
        ? skillRequirements.find(
            (ele) => ele.id == trainings?.skill_requirements_id
          )?.skills_required
        : null,
      allocated_additional_field: trainings?.allocated_additional_field ?? {},
    };
    return Array.isArray(setaTemplateFields) && setaTemplateFields.length > 0
      ? applySetaAliasesToTrainingRow(row, setaTemplateFields)
      : row;
  });

  const empDetails = { ...(empData?.data || {}) };
  // Ensure common aliases across demographic column naming differences (e.g. merSETA Employee vs WSP)
  if (!empDetails.id_number && (empDetails.employee_id || empData?.emp_id)) {
    empDetails.id_number = empDetails.employee_id || empData?.emp_id;
  }
  if (!empDetails.gender_code && (empDetails.gender || empData?.gender)) {
    empDetails.gender_code = empDetails.gender || empData?.gender;
  }
  if (!empDetails.equity_code && (empDetails.equity || empData?.race)) {
    empDetails.equity_code = empDetails.equity || empData?.race;
  }
  if (!empDetails.employment_type_code && (empDetails.employment_type || empData?.employment_type)) {
    empDetails.employment_type_code = empDetails.employment_type || empData?.employment_type;
  }
  if (!empDetails.nationality_code && empDetails.nationality) {
    empDetails.nationality_code = empDetails.nationality;
  }

  return {
    empDetails,
    trainingsAllocated,
    skillRequirements: Array.isArray(skillRequirements) ? skillRequirements : [],
    emp_id: empData?.emp_id,
    dept_id: empData?.department_id,
  };
}

// =============================================================================
// /helper prompt, response parsing, parser execution
// =============================================================================

/** Default system prompt for /helper when building Skills Planner → SETA `functionBody`. */
export const SKILLS_PLANNER_HELPER_PROMPT = `You are a senior JavaScript engineer building data transformers for a Skills Planner → SETA export pipeline.

The user message is JSON with two objects:
1) **inputSchema** — describes the SOURCE record (see structure below).
2) **outputSchema** — describes the TARGET row(s) (exact export field names and types).

## inputSchema shape (expected)
- \`version\`: number
- \`recordDescription\`: string (human summary)
- \`properties\`: object whose keys are top-level input keys (e.g. empDetails, trainingsAllocated, emp_id)
  - For \`type: "object"\`: include \`fields\`: [ { name, type, required?, description? } ]
  - For \`type: "array"\`: include \`itemFields\` for one element’s fields
  - Scalar properties: { type: "string"|"number"|"boolean", description? }
- \`dynamics.employmentTypes\`: optional — for unemployed/stipend rules only

## outputSchema shape (expected)
- \`version\`: number
- \`description\`: string
- \`rules\`: string array (business rules)
- \`transformMode\`: optional. If \`"oneRowPerTrainingItem"\`, one input record with \`trainingsAllocated[]\` must produce **one output object per training** (return an **array** of objects from \`functionBody\`).
- \`fields\`: array of:
  - \`fieldName\`: **exact** key in the output object (SETA column name, include brackets/spaces as given)
  - \`type\`: e.g. text, number, dropdown, date
  - \`fieldDescription\`: optional

Dropdown/export fields: **copy the value on the input record** (\`input.empDetails\`, training row, or \`allocated_additional_field\`). Dropdown catalogues are not in this message; label/value alignment is applied when the parser runs in the browser.

## Your response (critical)
Return **only** valid JSON. No markdown, no code fences, no commentary.

{
  "name": "short-id-slug",
  "description": "What you map from inputSchema to outputSchema.fields",
  "functionBody": "/* JavaScript body for new Function('input', functionBody) */"
}

## functionBody rules
- Parameter is \`input\` — one formatedData record (empDetails, trainingsAllocated, skillRequirements, emp_id, dept_id).
- Training rows may include both catalogue keys (\`name\`, \`provider\`, …) and SETA \`fieldName\` keys when the pipeline merged them; use whichever matches outputSchema.
- If outputSchema.transformMode is \`oneRowPerTrainingItem\` and \`input.trainingsAllocated\` is a non-empty array: **return an array** of plain objects, one per training, same order as trainingsAllocated, each object having **every** outputSchema.fields[].fieldName.
- If \`trainingsAllocated\` is empty: return **one** object (array of one element or single object — host accepts both).
- Use defensive coding: optional chaining, Number() for numbers.
- No fetch/import/require/eval. No DOM.

Respond with JSON only.`;

export function normalizeTransformResult(raw) {
  if (raw === undefined || raw === null) return [];
  return Array.isArray(raw) ? raw : [raw];
}

export function extractParserSpec(content) {
  if (content == null) return null;
  if (typeof content === "object" && !Array.isArray(content)) {
    if (content.functionBody && typeof content.functionBody === "string") {
      return content;
    }
    if (content.parser && typeof content.parser === "object") {
      return content.parser;
    }
  }
  if (typeof content === "string") {
    let s = content.trim();
    if (s.includes("```")) {
      s = s.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();
    }
    try {
      const parsed = JSON.parse(s);
      return extractParserSpec(parsed);
    } catch {
      return null;
    }
  }
  return null;
}

export function postSkillsPlannerHelper(prompt, messagePayload) {
  return postBackendHelperContent({ prompt, message: messagePayload });
}

/**
 * Run parser functionBody on one or many formatedData records; returns flattened output rows.
 * @param {string} functionBody
 * @param {object|object[]} records
 * @returns {object[]}
 */
export function runParserOnRecords(functionBody, records) {
  const fn = new Function("input", functionBody);
  const list = Array.isArray(records) ? records : [records];
  const chunks = list.map((row, i) => {
    try {
      return normalizeTransformResult(fn(row));
    } catch (err) {
      throw new Error(`Record ${i}: ${err.message}`);
    }
  });
  return chunks.flat();
}

/**
 * Expected flattened row count: one per training allocation, same order as
 * generateTraining edge merge (flatMap trainingsAllocated).
 */
export function expectedParserRowCountFromFormated(formatedRecords) {
  if (!Array.isArray(formatedRecords)) return 0;
  return formatedRecords.reduce(
    (n, emp) =>
      n +
      (Array.isArray(emp?.trainingsAllocated) ? emp.trainingsAllocated.length : 0),
    0
  );
}

function isSetaDropdownField(field) {
  return String(field?.type ?? "").toLowerCase() === "dropdown";
}

/**
 * Match raw value to SETA template option (label or value, case-insensitive).
 * Prefers export label when options are { label, value } objects.
 * @param {unknown} rawValue
 * @param {Array} allowedValues — from enriched setaTemplate field
 * @returns {unknown}
 */
export function normalizeSetaDropdownValue(rawValue, allowedValues) {
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return rawValue;
  }
  if (!Array.isArray(allowedValues) || allowedValues.length === 0) {
    return rawValue;
  }

  const raw = String(rawValue).trim();
  const rawLc = raw.toLowerCase();

  for (const opt of allowedValues) {
    if (opt != null && typeof opt === "object" && !Array.isArray(opt)) {
      const label =
        opt.label != null && opt.label !== "" ? String(opt.label).trim() : "";
      const value =
        opt.value != null && opt.value !== "" ? String(opt.value).trim() : "";
      if (label && label.toLowerCase() === rawLc) return label;
      if (value && value.toLowerCase() === rawLc) {
        return label || value;
      }
    } else {
      const s = opt != null ? String(opt).trim() : "";
      if (s && s.toLowerCase() === rawLc) return s;
    }
  }

  return rawValue;
}

/**
 * Normalize dropdown columns on one parser output row using SETA template metadata.
 * @param {object} row
 * @param {Array} setaTemplateFields — enriched template fields (includes static allowedValues)
 */
export function normalizeParserOutputRowDropdowns(row, setaTemplateFields) {
  if (!row || typeof row !== "object" || !Array.isArray(setaTemplateFields)) {
    return row;
  }

  for (const field of setaTemplateFields) {
    if (!field?.fieldName || !isSetaDropdownField(field)) continue;
    const c = field.config || {};
    if (!c.generatePlan && !c.isAdditionalField) continue;

    const key = field.fieldName;
    const current = row[key];
    if (current === undefined || current === null || current === "") continue;

    row[key] = normalizeSetaDropdownValue(current, field.allowedValues);
  }

  return row;
}

/**
 * Merge `allocated_additional_field`, spread additional keys, force cost field, and
 * normalize dropdown values — mirrors post-process in generateStructuredPlan plus
 * deterministic SETA dropdown alignment at parse time.
 * @param {object[]} transformedRows — parser output (same length as flattened trainings)
 * @param {object[]} formatedRecords — formatedData records used for the parser
 * @param {string} [costFieldName] — SETA training cost field name from template
 * @param {Array} [setaTemplateFields] — enriched SETA template fields for dropdown normalization
 * @returns {{ rows: object[], flattenedCosts: (number|undefined)[] }}
 */
export function mergeParserOutputWithAllocationDefaults(
  transformedRows,
  formatedRecords,
  costFieldName,
  setaTemplateFields = null
) {
  const flattenedAdditionalFields = formatedRecords.flatMap((emp) =>
    (emp.trainingsAllocated || []).map((t) => t.allocated_additional_field ?? {})
  );
  const flattenedCosts = formatedRecords.flatMap((emp) =>
    (emp.trainingsAllocated || []).map((t) => t.cost)
  );
  const flattenedEmployees = formatedRecords.flatMap((emp) =>
    (emp.trainingsAllocated || []).map(() => emp.empDetails ?? {})
  );
  const flattenedTrainings = formatedRecords.flatMap((emp) =>
    emp.trainingsAllocated || []
  );

  const rows = transformedRows.map((r) => ({ ...r }));
  for (let i = 0; i < rows.length; i++) {
    const additional = flattenedAdditionalFields[i] ?? {};
    rows[i].allocated_additional_field = additional;
    if (
      additional &&
      typeof additional === "object" &&
      !Array.isArray(additional)
    ) {
      Object.keys(additional).forEach((key) => {
        if (rows[i][key] === undefined || rows[i][key] === null) {
          rows[i][key] = additional[key];
        }
      });
    }
    if (
      costFieldName &&
      flattenedCosts[i] != null &&
      flattenedCosts[i] !== undefined
    ) {
      rows[i][costFieldName] = flattenedCosts[i];
    }

    // Defensive fallback: populate any missing demographic keys from the employee details
    const empDetails = flattenedEmployees[i] || {};
    const trRow = flattenedTrainings[i] || {};
    if (setaTemplateFields?.length) {
      for (const f of setaTemplateFields) {
        const fn = f.fieldName;
        if (!fn) continue;
        const currentVal = rows[i][fn];
        if (currentVal === undefined || currentVal === null || currentVal === "") {
          if (f.defaultValue !== undefined && f.defaultValue !== null && f.defaultValue !== "") {
            rows[i][fn] = f.defaultValue;
          } else if (empDetails[fn] !== undefined && empDetails[fn] !== null && empDetails[fn] !== "") {
            rows[i][fn] = empDetails[fn];
          } else if (trRow[fn] !== undefined && trRow[fn] !== null && trRow[fn] !== "") {
            rows[i][fn] = trRow[fn];
          }
        }
      }
      normalizeParserOutputRowDropdowns(rows[i], setaTemplateFields);
    }
  }
  return { rows, flattenedCosts };
}
